// ═══════════════════════════════════════════════════════════════════════
// colorSystem.js – Farben und Symbole der Memo-Karten
// ═══════════════════════════════════════════════════════════════════════
// Hier wird berechnet, welche Rahmenfarbe und welche Symbole jede Person bekommt.
// Das System (vom obersten zum untersten Stockwerk des Stammbaums):
//   • Gen0 (z.B. Grosseltern): mehrfarbiger Rahmen – die Farben aller ihrer Kinder-Familien
//   • Gen1 (deren Kinder): jedes Kind bekommt eine eigene Familienfarbe; Partner
//     und alle Nachkommen erben diese Farbe
//   • Gen2 (Enkel): Symbole (Quadrat, Kreis, Dreieck …) nach Geburtsreihenfolge
//     innerhalb ihrer Familie; ihre Eltern (Gen1) zeigen die Symbole ihrer Kinder
//   • Gen3 (Urenkel) mit Partner: zusätzlich ein Umriss-Symbol (outlineSymbol)
// So kann ein Kind am Rahmen erkennen, zu welchem Familienzweig jemand gehört,
// und an den Symbolen, wer zu wem gehört (siehe Extrakarte „Legende“).
//
// Begriffe: „id“ = eindeutige Kennung einer Person. Map = Nachschlagetabelle
// (Schlüssel → Wert), Set = Menge ohne doppelte Einträge.
// Farben und Symbole werden als Nummer (Index) gespeichert und erst am Schluss
// in die echte Farbe / den Symbolnamen übersetzt.
import { assignGenerations } from './treeLayout.js';

// Die Familienfarben in der Reihenfolge, in der sie vergeben werden.
export const FAMILY_COLORS = [
  '#1540dc', '#ff9600', '#09ad01', '#ff0000', '#e926e7', '#17e06c',
  '#00bcd4', '#FFD600', '#7B00FF',
];

// Die Formen der Symbole in Vergabe-Reihenfolge (gezeichnet in PersonCard.jsx).
export const SYMBOLS = [
  'square', 'circle', 'triangle', 'pentagon', 'diamond',
  'rectangle', 'trapezoid', 'parallelogram',
];

/**
 * Bestimmt, welche Generation welche Formatierung bekommt.
 * Oberste Gen = Gen0 (Mehrfarben-Rahmen), dann Gen1 (Farben), Gen2 (Symbole), Gen3 (Outline).
 */
export function getColorLevels(persons, relationships, genMap = null) {
  // Personen nach Generation (Stockwerk im Stammbaum) gruppieren.
  if (!persons.length) return null;
  const gm = genMap || assignGenerations(persons, relationships);
  const byGen = new Map();
  gm.forEach((g, id) => {
    if (!byGen.has(g)) byGen.set(g, []);
    byGen.get(g).push(id);
  });
  const sortedGens = [...byGen.keys()].sort((a, b) => a - b);
  // Mit nur einer Generation gibt es keine Farben.
  if (sortedGens.length < 2) return null;
  const top = byGen.get(sortedGens[0]) || [];
  const parentsOf = new Map();
  relationships.forEach(r => {
    if (r.type !== 'parent-child') return;
    if (!parentsOf.has(r.to)) parentsOf.set(r.to, []);
    parentsOf.get(r.to).push(r.from);
  });
  const gen0    = new Set(top);
  const gen1ids = byGen.get(sortedGens[1]) || [];
  return {
    gen0,
    gen1ids,
    gen2ids: byGen.get(sortedGens[2]) || [],
    gen3ids: byGen.get(sortedGens[3]) || [],
    // gen1siblings = die Kinder der obersten Generation – sie bestimmen die Familienfarben.
    gen1siblings: gen1ids.filter(id => (parentsOf.get(id) || []).some(p => gen0.has(p))),
  };
}

// Hauptfunktion. Gibt eine Map zurück: Personen-id → { color, borderColors,
// symbols, outlineSymbol, isGen1 }. Wird überall verwendet, wo Karten gezeigt
// oder gezeichnet werden. customColors = vom Kunden selbst gewählte Farben.
// extension = bei einer Erweiterung (siehe extension.js): übernommene Personen behalten
// Farbe und Symbole aus dem Original, neue Familien bekommen nur Farben, die im
// Original nicht vorkommen.
export function computeColorSystem(persons, relationships, customColors = {}, extension = null) {
  if (!persons.length) return new Map();

  const genMap     = assignGenerations(persons, relationships);
  // Nachschlagetabellen aus den Beziehungen aufbauen:
  //   parentsOf = Eltern einer Person, childrenOf = Kinder,
  //   currentPartnerOf = aktuelle Partner, exPartnerOf = frühere Partner.
  const parentsOf  = new Map();
  const childrenOf = new Map();
  const currentPartnerOf = new Map();
  const exPartnerOf      = new Map();

  relationships.forEach(r => {
    if (r.type === 'parent-child') {
      if (!parentsOf.has(r.to))    parentsOf.set(r.to, []);
      if (!childrenOf.has(r.from)) childrenOf.set(r.from, []);
      parentsOf.get(r.to).push(r.from);
      childrenOf.get(r.from).push(r.to);
    } else if (r.type === 'partner') {
      if (!currentPartnerOf.has(r.from)) currentPartnerOf.set(r.from, []);
      if (!currentPartnerOf.has(r.to))   currentPartnerOf.set(r.to, []);
      currentPartnerOf.get(r.from).push(r.to);
      currentPartnerOf.get(r.to).push(r.from);
    } else if (r.type === 'ex-partner') {
      if (!exPartnerOf.has(r.from)) exPartnerOf.set(r.from, []);
      if (!exPartnerOf.has(r.to))   exPartnerOf.set(r.to, []);
      exPartnerOf.get(r.from).push(r.to);
      exPartnerOf.get(r.to).push(r.from);
    }
  });

  const allPartnerOf = (id) => [
    ...(currentPartnerOf.get(id) || []),
    ...(exPartnerOf.get(id) || []),
  ];

  const personById = new Map(persons.map(p => [p.id, p]));
  const levels = getColorLevels(persons, relationships, genMap);
  if (!levels) {
    // Nur eine Generation: keine Farben – ausser den übernommenen Personen einer Erweiterung.
    const m = new Map();
    if (extension) (extension.lockedIds || []).forEach(id => {
      if (extension.lockedInfo?.[id]) m.set(id, { ...extension.lockedInfo[id], isGen1: false, locked: true });
    });
    return m;
  }
  const { gen0, gen1ids, gen2ids, gen3ids, gen1siblings } = levels;
  const gen1set = new Set(gen1ids);
  const gen2set = new Set(gen2ids);
  const gen3set = new Set(gen3ids);

  // ── FARBEN ────────────────────────────────────────────────────────────
  const colorMap = new Map();
  // Jedes Kind der obersten Generation bekommt eine Farbe: die selbst gewählte
  // (customColors) oder sonst der Reihe nach die nächste Farbe der Liste.
  const lockedInfo = (extension && extension.lockedInfo) || {};
  const lockedIdx = id => {
    const c = lockedInfo[id]?.color;
    const i = c ? FAMILY_COLORS.findIndex(f => f.toLowerCase() === c.toLowerCase()) : -1;
    return i >= 0 ? i : undefined;
  };
  if (!extension) {
    gen1siblings.forEach((id, i) => {
      const custom = customColors[id];
      const ci = (custom !== undefined && custom >= 0 && custom < FAMILY_COLORS.length) ? custom : i % FAMILY_COLORS.length;
      colorMap.set(id, ci);
    });
  } else {
    // Erweiterung: übernommene Personen behalten ihre Farbe; alle anderen bekommen
    // der Reihe nach die nächste Farbe, die weder im Original noch hier schon vergeben ist.
    const taken = new Set((extension.baseColors || [])
      .map(c => FAMILY_COLORS.findIndex(f => f.toLowerCase() === String(c).toLowerCase())).filter(i => i >= 0));
    gen1siblings.forEach(id => { const li = lockedIdx(id); if (li !== undefined) colorMap.set(id, li); });
    // zuerst die selbst gewählten Farben – nur, wenn sie nicht im Original vorkommen und noch frei sind
    gen1siblings.forEach(id => {
      if (colorMap.has(id)) return;
      const custom = customColors[id];
      if (custom !== undefined && custom >= 0 && custom < FAMILY_COLORS.length && !taken.has(custom)) { colorMap.set(id, custom); taken.add(custom); }
    });
    // dann alle übrigen der Reihe nach
    let next = 0;
    gen1siblings.forEach(id => {
      if (colorMap.has(id)) return;
      while (taken.has(next) && next < FAMILY_COLORS.length - 1) next++;
      colorMap.set(id, next);
      taken.add(next);
    });
  }

  // Wenn zwei gen1-Geschwister (beide mit Eltern in gen0) Partner sind,
  // bekommen sie die gleiche Farbe — niedrigerer Farbindex gewinnt.
  const gen1sibSet = new Set(gen1siblings);
  let reconChanged = true;
  while (reconChanged) {
    reconChanged = false;
    gen1siblings.forEach(id => {
      const ci = colorMap.get(id);
      allPartnerOf(id).filter(pid => gen1sibSet.has(pid)).forEach(pid => {
        const pci = colorMap.get(pid);
        if (pci === undefined || pci === ci) return;
        if (ci < pci) {
          colorMap.set(pid, ci);
        } else {
          colorMap.set(id, pci);
        }
        reconChanged = true;
      });
    });
  }

  // Übernommene Personen (Erweiterung) behalten immer ihre Farbe aus dem Original.
  gen1siblings.forEach(id => { const li = lockedIdx(id); if (li !== undefined) colorMap.set(id, li); });

  // Partner der Familien-Gründer bekommen deren Farbe.
  gen1siblings.forEach(id => {
    const ci = colorMap.get(id);
    allPartnerOf(id).forEach(pid => { if (!colorMap.has(pid)) colorMap.set(pid, ci); });
  });

  // Danach wird die Farbe nach unten weitergegeben: Kinder und deren Partner erben.
  // „BFS“ (Breitensuche) heisst: Stockwerk um Stockwerk von oben nach unten.
  // BFS: gen1siblings + ihre Partner als Startpunkte
  const bfsStart = [...gen1siblings];
  gen1siblings.forEach(id => allPartnerOf(id).forEach(pid => { if (!bfsStart.includes(pid)) bfsStart.push(pid); }));
  const bfsQ = [...bfsStart], bfsV = new Set(bfsStart);
  while (bfsQ.length) {
    const cur = bfsQ.shift(), ci = colorMap.get(cur);
    (childrenOf.get(cur) || []).forEach(child => {
      if (!colorMap.has(child)) colorMap.set(child, ci);
      allPartnerOf(child).forEach(p => { if (!colorMap.has(p)) colorMap.set(p, ci); });
      if (!bfsV.has(child)) { bfsV.add(child); bfsQ.push(child); }
    });
  }

  // Partner, die noch keine Farbe haben, bekommen die Farbe ihres Partners – z.B. der
  // Expartner einer eingeheirateten Person (Anias), mit dem sie ein Kind hat.
  // Gilt nicht für die oberste Generation (die hat den mehrfarbigen Rahmen).
  for (let changedP = true; changedP;) {
    changedP = false;
    for (const p of persons) {
      if (colorMap.has(p.id) || gen0.has(p.id)) continue;
      const c = allPartnerOf(p.id).map(x => colorMap.get(x)).find(v => v !== undefined);
      if (c !== undefined) { colorMap.set(p.id, c); changedP = true; }
    }
  }

  // ── MULTI-RAHMEN für Gen0 ─────────────────────────────────────────────
  // Die oberste Generation bekommt alle Farben ihrer Kinder als Rahmen.
  const borderColorsMap = new Map();
  gen0.forEach(id => {
    const cols = [...new Set(
      (childrenOf.get(id) || []).map(c => colorMap.get(c)).filter(c => c !== undefined)
    )];
    if (cols.length) borderColorsMap.set(id, cols);
  });

  // ── SYMBOLE für Gen1 ─────────────────────────────────────────────────
  // Wird NACH gen2symMap berechnet (siehe unten), damit die echten Gen2-Symbole
  // der Kinder verwendet werden können. Hier nur Deklaration.
  const symbolMap = new Map(); // gen1 personId -> symIdx[]

  // ── SYMBOLE für Gen2 ──────────────────────────────────────────────────
  // Gen2-Geschwister (direkte Kinder von Gen1) bekommen Symbole nach Geburtsreihenfolge
  // pro Farbe neu beginnend bei Quadrat.
  // Expartner von Gen1 bekommen das nächste verfügbare Symbol nach allen Geschwistern.
  const gen2symMap = new Map(); // personId -> symIdx (number) or symIdx[] (array)

  // Schritt 1: Symbole an Gen2-Geschwister vergeben
  const colorToGen2sibs = new Map(); // colorIdx -> [personId]
  gen2ids.forEach(id => {
    const hasGen1Parent = (parentsOf.get(id) || []).some(p => gen1set.has(p));
    if (!hasGen1Parent) return;
    const ci = colorMap.get(id);
    if (ci === undefined) return;
    if (!colorToGen2sibs.has(ci)) colorToGen2sibs.set(ci, []);
    colorToGen2sibs.get(ci).push(id);
  });

  colorToGen2sibs.forEach((ids, ci) => {
    ids.sort((a, b) => {
      const da = personById.get(a)?.birthDate || '';
      const db = personById.get(b)?.birthDate || '';
      return da < db ? -1 : da > db ? 1 : 0;
    });
    ids.forEach((id, i) => gen2symMap.set(id, i % SYMBOLS.length));
  });

  // Schritt 2–4: Partner und Expartner der Gen2-Geschwister
  // Regel: Jede Partnerschaft braucht ein gemeinsames Symbol – auch ohne Kinder –,
  // damit man die beiden im Memo zusammenfinden kann.
  //   • Nur EIN Partner (aktuell oder früher): er bekommt einfach das Symbol des
  //     Geschwisters (Beispiel: Luc ▲ und seine Expartnerin Shakira ▲).
  //   • Mehrere Partner: der aktuelle Partner (ohne aktuellen: der früheste Expartner)
  //     bekommt das Symbol des Geschwisters, jeder weitere ein neues Symbol der
  //     Familie. Das Geschwister zeigt dann alle diese Symbole.
  // pairSym merkt sich das Symbol jeder Partnerschaft – die Kinder erben es (Schritt 5).
  const pairSym = new Map(); // "idA|idB" → symIdx
  const pairKey = (a, b) => [a, b].sort().join('|');
  const colorNextSym = new Map();
  colorToGen2sibs.forEach((ids, ci) => colorNextSym.set(ci, ids.length)); // startet nach den Geschwistern
  const firstCommonKid = (a, b) => {
    const kids = (childrenOf.get(a) || []).filter(k => (parentsOf.get(k) || []).includes(b));
    return kids.map(k => personById.get(k)?.birthDate || '9999').sort()[0] || '9999';
  };

  // Geschwister nach Geburtsdatum (bestimmt die Reihenfolge der neuen Symbole)
  const gen2sibsSorted = [...colorToGen2sibs.values()].flat().sort((a, b) => {
    const da = personById.get(a)?.birthDate || '';
    const db = personById.get(b)?.birthDate || '';
    return da < db ? -1 : da > db ? 1 : 0;
  });
  gen2sibsSorted.forEach(sibId => {
    const own = gen2symMap.get(sibId);
    const ci = colorMap.get(sibId);
    const cur = (currentPartnerOf.get(sibId) || []);
    const exs = (exPartnerOf.get(sibId) || []).slice()
      .sort((a, b) => (firstCommonKid(sibId, a) < firstCommonKid(sibId, b) ? -1 : 1));
    const partners = [...cur, ...exs];   // der erste behält das eigene Symbol
    const mine = [own];
    partners.forEach((pid, i) => {
      let sym;
      if (pairSym.has(pairKey(sibId, pid))) sym = pairSym.get(pairKey(sibId, pid));
      else if (i === 0) sym = own;
      else {
        const next = colorNextSym.get(ci) ?? 0;
        sym = next % SYMBOLS.length;
        colorNextSym.set(ci, next + 1);
      }
      pairSym.set(pairKey(sibId, pid), sym);
      if (!mine.includes(sym)) mine.push(sym);
      // Partner bekommt das Symbol (ist er selbst ein Geschwister mit eigenem Symbol,
      // zeigt er es zusätzlich)
      const ps = gen2symMap.get(pid);
      if (ps === undefined) gen2symMap.set(pid, sym);
      else if (Array.isArray(ps)) { if (!ps.includes(sym)) ps.push(sym); }
      else if (ps !== sym) gen2symMap.set(pid, [ps, sym]);
    });
    gen2symMap.set(sibId, mine.length > 1 ? mine : own);
  });

  // Schritt 5: BFS — Kinder erben das Symbol ihrer Eltern-Partnerschaft
  // (Kinder mit einem Expartner also das Symbol dieser Partnerschaft).
  const symQueue = [...gen2symMap.keys()];
  const symVisited = new Set(symQueue);
  while (symQueue.length) {
    const cur = symQueue.shift();
    const curSym = gen2symMap.get(cur);
    const symIdx = Array.isArray(curSym) ? curSym[0] : curSym;
    (childrenOf.get(cur) || []).forEach(child => {
      if (symVisited.has(child)) return;
      const other = (parentsOf.get(child) || []).find(p => p !== cur);
      const ps = other !== undefined ? pairSym.get(pairKey(cur, other)) : undefined;
      gen2symMap.set(child, ps !== undefined ? ps : symIdx);
      symVisited.add(child);
      symQueue.push(child);
    });
  }

  // Schritt 6: Gen2-Personen UND ihre Partner zeigen alle Symbole ihrer gemeinsamen Gen3-Kinder
  const gen2andPartners = new Set([...gen2set]);
  gen2set.forEach(id => {
    (currentPartnerOf.get(id) || []).forEach(pid => gen2andPartners.add(pid));
    (exPartnerOf.get(id) || []).forEach(pid => gen2andPartners.add(pid));
  });

  gen2andPartners.forEach(id => {
    if (!gen2symMap.has(id)) return;
    // Nur die eigenen Kinder dieser Person (nicht die Kinder, die ein Partner mit
    // jemand anderem hat – sonst bekäme eine neue Partnerin das Symbol des Expartners).
    const allChildren = new Set(childrenOf.get(id) || []);
    const childSyms = new Set(
      [...allChildren]
        .filter(k => gen3set.has(k) && gen2symMap.has(k))
        .map(k => {
          const s = gen2symMap.get(k);
          return Array.isArray(s) ? s[0] : s;
        })
    );
    childSyms.forEach(s => {
      const cur = gen2symMap.get(id);
      if (!Array.isArray(cur)) {
        if (cur !== s) gen2symMap.set(id, [cur, s]);
      } else if (!cur.includes(s)) {
        cur.push(s);
      }
    });
  });

  // ── SYMBOLE für Gen1 (nach Gen2-Berechnung) ──────────────────────────
  // Gen1-Personen zeigen die echten Symbole ihrer direkten Gen2-Kinder.
  // Berechnung hier, damit gen2symMap bereits vollständig ist.
  gen1ids.forEach(id => {
    const myGen = genMap.get(id) ?? 0;
    const kids = (childrenOf.get(id) || []).filter(k => (genMap.get(k) ?? 0) > myGen);
    if (!kids.length) return;

    // Kinder nach Geburtsdatum sortieren für konsistente Reihenfolge
    const kidsSorted = [...kids].sort((a, b) => {
      const da = personById.get(a)?.birthDate || '';
      const db = personById.get(b)?.birthDate || '';
      return da < db ? -1 : da > db ? 1 : 0;
    });

    // Symbole der Kinder aus gen2symMap lesen (dedupliziert, Reihenfolge beibehalten)
    const seen = new Set();
    const childSyms = [];
    kidsSorted.forEach(k => {
      const s = gen2symMap.get(k);
      const si = s !== undefined ? (Array.isArray(s) ? s[0] : s) : null;
      if (si !== null && !seen.has(si)) { seen.add(si); childSyms.push(si); }
    });

    if (childSyms.length > 0) {
      symbolMap.set(id, childSyms);
    } else {
      // Fallback: sequentiell (Kinder haben noch kein Gen2-Symbol)
      symbolMap.set(id, kids.map((_, i) => i % SYMBOLS.length));
    }
  });

  // Gen1-Paare ohne gemeinsame Kinder (z.B. Claudia und Volkhard, oder Sabine mit
  // ihrem neuen Partner Beat) bekommen ein gemeinsames Symbol, damit man sie im Memo
  // zusammenfinden kann. Es darf in dieser Familienfarbe noch nicht vorkommen –
  // weder bei der Person noch bei ihren Kindern/Enkeln (Gen2) –, sonst würde man
  // den Partner für einen Elternteil halten.
  const usedInColor = new Map(); // colorIdx -> Set(symIdx)
  gen2symMap.forEach((sv, pid) => {
    const c = colorMap.get(pid);
    if (c === undefined) return;
    if (!usedInColor.has(c)) usedInColor.set(c, new Set());
    (Array.isArray(sv) ? sv : [sv]).forEach(x => usedInColor.get(c).add(x));
  });
  const gen1Pairs = new Set();
  gen1ids.forEach(id => {
    allPartnerOf(id).forEach(pid => {
      if (genMap.get(pid) !== genMap.get(id)) return;
      const key = pairKey(id, pid);
      if (gen1Pairs.has(key)) return;
      gen1Pairs.add(key);
      const common = (childrenOf.get(id) || []).some(k => (parentsOf.get(k) || []).includes(pid));
      if (common) return;
      const c = colorMap.get(id) ?? colorMap.get(pid);
      if (!usedInColor.has(c)) usedInColor.set(c, new Set());
      const used = new Set([...usedInColor.get(c), ...(symbolMap.get(id) || []), ...(symbolMap.get(pid) || [])]);
      let si = 0;
      while (used.has(si) && si < SYMBOLS.length - 1) si++;
      usedInColor.get(c).add(si);
      [id, pid].forEach(x => {
        const list = symbolMap.get(x) || [];
        if (!list.includes(si)) list.push(si);
        symbolMap.set(x, list);
      });
    });
  });

  // ── OUTLINE-SYMBOLE für Gen3 ──────────────────────────────────────────
  // Gen3-Geschwister mit aktuellem Partner oder Expartner bekommen ein Outline-Symbol.
  // Zähler pro Elterngruppe, startet bei 0 (unabhängig von Gen2-Symbolen).
  const outlineSymMap = new Map();

  // Nur echte Gen3-Geschwister (haben Eltern in Gen2)
  const gen3sibs = gen3ids.filter(id =>
    (parentsOf.get(id) || []).some(p => gen2set.has(p))
  );

  // Sortiere nach Geburtsdatum
  const gen3sibsSorted = [...gen3sibs].sort((a, b) => {
    const da = personById.get(a)?.birthDate || '';
    const db = personById.get(b)?.birthDate || '';
    return da < db ? -1 : da > db ? 1 : 0;
  });

  // Pro Elterngruppe einen Zähler
  const outlineCounter = new Map(); // parentKey -> nextIdx

  gen3sibsSorted.forEach(sibId => {
    const hasPartner = (currentPartnerOf.get(sibId) || []).length > 0
                    || (exPartnerOf.get(sibId) || []).length > 0;
    if (!hasPartner) return;

    const parentKey = (parentsOf.get(sibId) || [])
      .filter(p => gen2set.has(p))
      .sort().join('|') || sibId;

    const nextSi = (outlineCounter.get(parentKey) ?? 0) % SYMBOLS.length;
    outlineCounter.set(parentKey, (outlineCounter.get(parentKey) ?? 0) + 1);

    const filledSym = gen2symMap.has(sibId)
      ? (Array.isArray(gen2symMap.get(sibId)) ? gen2symMap.get(sibId)[0] : gen2symMap.get(sibId))
      : undefined;

    // Geschwister bekommt Outline
    outlineSymMap.set(sibId, nextSi);

    // Aktuelle Partner: gleiches Outline + Filled-Symbol des Geschwister
    (currentPartnerOf.get(sibId) || []).forEach(pid => {
      outlineSymMap.set(pid, nextSi);
      if (!gen2symMap.has(pid) && filledSym !== undefined) gen2symMap.set(pid, filledSym);
    });

    // Expartner: gleiches Outline + Filled-Symbol des Geschwister
    (exPartnerOf.get(sibId) || []).forEach(exId => {
      outlineSymMap.set(exId, nextSi);
      if (!gen2symMap.has(exId) && filledSym !== undefined) gen2symMap.set(exId, filledSym);
    });
  });

  // Outline-Symbole an Gen4+ weitergeben
  gen3sibs.forEach(sibId => {
    const outSi = outlineSymMap.get(sibId);
    if (outSi === undefined) return;
    (childrenOf.get(sibId) || []).forEach(function propagate(child) {
      if (!outlineSymMap.has(child)) {
        outlineSymMap.set(child, outSi);
        (childrenOf.get(child) || []).forEach(propagate);
      }
    });
  });

  // ── ERGEBNIS ──────────────────────────────────────────────────────────
  // Am Schluss für jede Person die Nummern in echte Farben und Symbolnamen übersetzen.
  const result = new Map();
  persons.forEach(p => {
    let ci = colorMap.get(p.id);
    const borders = borderColorsMap.get(p.id);
    const syms    = symbolMap.get(p.id);
    const sym2    = gen2symMap.get(p.id);
    const outSym  = outlineSymMap.get(p.id);
    // Falls keine eigene Farbe: von Elternteil erben
    if (ci === undefined && sym2 !== undefined) {
      for (const par of (parentsOf.get(p.id) || [])) {
        const parCi = colorMap.get(par);
        if (parCi !== undefined) { ci = parCi; break; }
      }
    }

    if (ci !== undefined || borders || syms || sym2 !== undefined) {
      const color = ci !== undefined ? FAMILY_COLORS[ci] : null;
      let symbols = null;
      if (syms) {
        symbols = syms.map(i => SYMBOLS[i]);
      } else if (Array.isArray(sym2)) {
        symbols = [...new Set(sym2)].map(i => SYMBOLS[i]);
      } else if (sym2 !== undefined) {
        symbols = [SYMBOLS[sym2]];
      }
      const outlineSymbol = outSym !== undefined ? SYMBOLS[outSym] : null;
      result.set(p.id, { color, borderColors: borders ? borders.map(i => FAMILY_COLORS[i]) : null, symbols, outlineSymbol, isGen1: gen1set.has(p.id) });
    }
  });
  // Erweiterung: übernommene Personen genau wie im Original (locked = grau anzeigen)
  if (extension) {
    (extension.lockedIds || []).forEach(id => {
      const li = lockedInfo[id];
      if (li) result.set(id, { ...li, isGen1: false, locked: true });
      else if (result.has(id)) result.set(id, { ...result.get(id), isGen1: false, locked: true });
    });
  }
  return result;
}
